// src/dataTypes/broadcastingManagment/types.ts


import { ThirdPartyStreamingChannel } from './types_thirdPartyStreamingSystem';

import { Media                      ,
         initializedMedia           } from '../../dataTypes/mediaManagment/types';


export interface BroadcastedMedia
 {
  id                    : number      ,
  mediaId               : number      ,
  broadcastDate         : Date | null ,
  status                : string      ,
  actualStartTime       : Date | null ,
  // Index de la piste audio choisie (0 = première piste). null = comportement par
  // défaut (première piste) — valable pour la quasi-totalité des médias, qui n'ont
  // qu'une seule piste audio. Voir VideoAudioTrack (api_video.ts) pour la liste des
  // pistes disponibles sur un média donné.
  audioTrackIndex       : number | null ,
  // « Jouer en boucle » : le microservice relance la vidéo à chaque fin naturelle,
  // indéfiniment, et retient les programmes qui la suivent (aucune heure de départ
  // calculable). Voir scheduleOperations.ts (setProgramLoop).
  loop                  : boolean ,
  // « Lire maintenant » : le programme a coupé la vidéo en cours et se pose PAR-DESSUS la
  // grille sans la décaler ; à sa fin, le microservice reprend la grille là où elle en
  // serait (seek). Ce qu'il recouvre est voilé dans BroadcastedMediaListV2.
  priority              : boolean ,
  // Lu dans ArchivedBroadcastedMedia (id négatif) : déjà joué, sauté ou en échec. Seules les
  // archives du jour liées à un « Lire maintenant » sont gardées (keepPriorityArchives).
  archived?             : boolean ,
 }

export interface EnrichedBroadcastedMedia extends BroadcastedMedia {

  media                 : Media       ,
  duration              : number      ,
  title                 : string      ,
  thumbnail             : string
 }

export interface TaggedEnrichedBroadcastedMedia extends EnrichedBroadcastedMedia {
  tag                   : string;
  oldTag                : string;
  selected              : boolean;
  // La boucle a été basculée localement, pas encore envoyée (PATCH .../loop). Distinct du
  // tag : un programme EN COURS garde son tag 'current' — on ne le reprogramme jamais.
  loopDirty?            : boolean;
  // Fin du tour en cours retenue à la désactivation de la boucle : les programmes suivants
  // y ont été recalés. Sert à annuler ce recalage si on réarme, et à le corriger à
  // l'enregistrement avec la fin exacte renvoyée par le microservice.
  loopCycleEnd?         : Date | null;
}

export const initializedBroadcastedMedia = (): BroadcastedMedia => (
 {
  id                    : 0    ,
  mediaId               : 0    ,
  broadcastDate         : null ,
  status                : ''   ,
  actualStartTime       : null ,
  audioTrackIndex       : null ,
  loop                  : false,
  priority              : false,
});

export const initializedEnrichedBroadcastedMedia = (): EnrichedBroadcastedMedia => ({
  ...initializedBroadcastedMedia() ,
  duration              : 0    ,
  title                 : ''   ,
  thumbnail             : ''   ,
  media                 : initializedMedia () ,
});

 export const initializedTaggedEnrichedBroadcastedMedia = (): TaggedEnrichedBroadcastedMedia => ({
  ... initializedEnrichedBroadcastedMedia() ,
  tag                   : "",
  oldTag                : "",
  selected              : false
});

//////////////////////////////////

export type StreamSource = 'video' | 'rtmp' | 'webrtc';

export interface SourceOption {
  value: StreamSource;
  label: string;
}

export const SOURCE_OPTIONS: SourceOption[] = [
  { value: 'video',  label: 'Mire / playlist'     },
  { value: 'rtmp',   label: 'Source externe RTMP' },
  { value: 'webrtc', label: 'Régie Tubemarket'    },
];

/** Ce que le serveur dit de la vidéo d'une chaîne (cf. StreamQualityProfile côté API) — pour l'affichage seulement. */
export interface VideoProfileInfo {
  name             : string ;
  width            : number ;
  height           : number ;
  fps              : number ;
  codec            : string ;
  videoBitrateKbps : number ;
  audioBitrateKbps : number ;
}

export interface BaseBroadcastingChannel
 {
  id                             : number                        ,
  uuid                           : string                        ,
  publicRef                      : string                        ,
  rtmpLogin?                     : string                        ,   // Publication RTMP sur live_{uuid} (OBS) — fiche détaillée seulement
  rtmpPassword?                  : string                        ,
  creationDate                   : Date | null ,
  name                           : string                        ,
  slug                           : string                        ,
  logoImageFile                  : string                        ,
  originalLogoImageFile          : string                        ,   // Nom du logo tel que téléversé — AFFICHAGE seulement : le fichier privé est
                                                                    // effacé sitôt les copies publiques écrites (admin-api, 19/08/2026).
  description                    : string                        ,
  active                         : boolean                       ,
  channelStatus                  : 'active' | 'restricted' | 'activating' | 'activating_restricted' | 'inactive' | 'deactivating' | 'deactivating_restricted' | 'error' ,
  activeScheduled                : boolean     ,
  activeStartDate                : Date | null ,     // Si non nul alors actif après cette date, sinon actif/inactif immédiatement
  activeEndDate                  : Date | null ,     // Si non nul alors actif après cette date, sinon actif/inactif immédiatement
  publicPortal                   : boolean                       ,
  ephemeral                      : boolean                       ,   // chaîne créée et pilotée par un autre système : la régie n y a pas accès (colonne posée à la main en base)
  orientation                    : 'landscape' | 'portrait'      ,   // sens du programme (1920×1080 ou 1080×1920), choisi à la CRÉATION dans l'onglet Configuration ; paysage par défaut
  videoProfile?                  : VideoProfileInfo              ,   // paramètres vidéo du programme (profil de l'hôte + sens), pour le tableau de bord ; absent d'un ancien serveur
  multiview                      : boolean                       ,   // pilotée par le bouton "Multi-angle" de la régie, exposée telle quelle au player Xtream
  ownerId?                       : number | null                 ,   // créateur propriétaire : un superadmin qui ouvre la régie d'un AUTRE créateur y reste observateur discret
  ownerUseFiligrane              : boolean                       ,   // reflet en lecture de Admin.useFiligrane du créateur propriétaire — jamais réglable depuis la chaîne
  chatModeration                 : number                        ,   // -1 : non autorisé, 0 : sans modération, 1 : standard, 2 : avancée, 3 : manuelle
  createdFromRegie               : boolean                       ,   // chaîne née dans cette régie (et non dans les apps) : seule à pouvoir ouvrir son plateau au public
  stageOpen                      : boolean                       ,   // « Ouvrir le plateau au public » : les spectateurs connectés du portail demandent à monter (2026-10-15)
  mode                           : string                        ,
  streamSource                   : StreamSource                   ,
  defaultStreamSource            : StreamSource                   ,
  pendingImageFile               : string                        ,
  originalPendingImageFile       : string                        ,   // Voir originalLogoImageFile.
  pendingSoundFile               : string                        ,
  originalPendingSoundFile       : string                        ,   // Voir originalLogoImageFile.
  showTitle                      : boolean                       ,
  showDate                       : boolean                       ,
  titleFont                      : string                        ,
  titleColor                     : string                        ,
  titleX                         : number                        ,
  titleY                         : number                        ,
  titleSize                      : number                        ,
  titleBoxOpacity                : number                        ,   // opacité 0-100 % du fond sombre derrière le nom sur la mire (0 = aucun)
  dateBoxOpacity                 : number                        ,   // idem pour l'heure
  titleBold                      : boolean                       ,
  titleUnderline                 : boolean                       ,
  titleItalic                    : boolean                       ,
  dateFont                       : string                        ,
  dateColor                      : string                        ,
  dateX                          : number                        ,
  dateY                          : number                        ,
  dateSize                       : number                        ,
  dateBold                       : boolean                       ,
  dateUnderline                  : boolean                       ,
  dateItalic                     : boolean                       ,
  timezone                       : string                        ,   // fuseau horaire IANA (ex: 'Europe/Paris') utilisé pour l'incrustation de l'heure sur la mire d'attente — colonne Symfony déjà présente sur l'entité mais pas encore lue/écrite par les contrôleurs Patch/Creation

  requiredUserStatusId           : number                        ,
  keywordsIds                    : number []                     ,
  categoriesIds                  : number []                     ,
  associatedProductIds           : number []                     ,
  thirdPartyStreamingChannels    : ThirdPartyStreamingChannel [] ,

  ingestionUrl                   : string                        ,
  watchingUrl                    : string                        ,
  ipTvGenericalUser              : string                        ,
  ipTvGenericalPwd               : string                        ,
  currentBroadcastedMediaId      : number                        ,
  currentBroadcastedMediaName    : string                        ,
  currentNumberOfConnections     : number                        ,
 }

export interface BroadcastingChannel extends BaseBroadcastingChannel
 {
  broadcastedMedias              : BroadcastedMedia []           
 }

export interface EnrichedBroadcastingChannel extends BaseBroadcastingChannel
 {
  broadcastedMedias              : EnrichedBroadcastedMedia []           
 }

export const initializedBaseBroadcastingChannel = (): BaseBroadcastingChannel => (
 {
  id                             : 0     ,
  uuid                           : ''    ,
  publicRef                      : ''    ,
  creationDate                   : null  ,
  name                           : ''    ,
  slug                           : ''    ,
  logoImageFile                  : ''    ,
  originalLogoImageFile          : ''    ,
  description                    : ''    ,
  active                         : false ,
  channelStatus                  : 'inactive' ,
  activeScheduled                : false     ,     
  activeStartDate                : null ,     // Si non nul alors actif après cette date, sinon actif/inactif immédiatement
  activeEndDate                  : null ,     // Si non nul alors actif après cette date, sinon actif/inactif immédiatement
  publicPortal                   : false ,
  ephemeral                      : false ,
  orientation                    : 'landscape' ,
  multiview                      : false ,
  ownerUseFiligrane              : false ,
  chatModeration                 : -1    ,     // chat fermé à la création, comme côté serveur
  createdFromRegie               : true  ,     // une chaîne créée depuis ce formulaire naît dans la régie
  stageOpen                      : false ,     // plateau fermé au public par défaut
  mode                           : ''    ,
  streamSource                   : 'video'    ,
  defaultStreamSource            : 'video'    ,
  pendingImageFile               : ''    ,
  originalPendingImageFile       : ''    ,
  pendingSoundFile               : ''    ,
  originalPendingSoundFile       : ''    ,
  showTitle                      : true ,
  showDate                       : true ,
  titleFont                      : 'Arial'   ,
  titleColor                     : '#FFFFFF' ,
  titleX                         : 0     ,
  titleY                         : 0     ,
  titleSize                      : 64    ,
  titleBoxOpacity                : 60    ,
  dateBoxOpacity                 : 60    ,
  titleBold                      : false ,
  titleUnderline                 : false ,
  titleItalic                    : false ,
  dateFont                       : 'Arial'   ,
  dateColor                      : '#FFFFFF' ,
  dateX                          : 0     ,
  dateY                          : 0     ,
  dateSize                       : 64    ,
  dateBold                       : false ,
  dateUnderline                  : false ,
  dateItalic                     : false ,
  timezone                       : 'Europe/Paris' ,

  requiredUserStatusId           : 0     ,
  keywordsIds                    : []    ,
  categoriesIds                  : []    ,
  associatedProductIds           : []    ,
  thirdPartyStreamingChannels    : []    ,

  ingestionUrl                   : ''    ,
  watchingUrl                    : ''    ,
  ipTvGenericalUser              : '',
  ipTvGenericalPwd               : '',  
  currentBroadcastedMediaId      : 0     ,
  currentBroadcastedMediaName    : ''    ,
  currentNumberOfConnections     : 0     ,
});

export const initializedBroadcastingChannel = (): BroadcastingChannel => (
 {
  ... initializedBaseBroadcastingChannel() ,
  broadcastedMedias : []           
});

export const initializedEnrichedBroadcastingChannel = (): EnrichedBroadcastingChannel => (
 {
  ... initializedBaseBroadcastingChannel() ,
  broadcastedMedias : []           
});


////////////////

export interface BroadcastingChannelCategory
 {
  id            : number  ,
  name          : string  ,
  rank          : number  ,
  visible       : boolean ,
 }

export const initializedBroadcastingChannelCategory = (): BroadcastingChannelCategory => (
 {
  id            : 0     ,
  name          : ""    ,
  rank          : 0     ,
  visible       : false 
});


/////////////////////////////// Type partie customer

export interface CurrentStreamMetadata {
  status: "playing" | "waiting" | "idle";
  broadcasted_media_id: number;
  media_id: number;
  media_uuid: string;
  // Optionnel tant que le microservice ffmpeg n'est pas redéployé : il ne
  // l'enverra qu'à partir de là. Le « ? » fait dire au compilateur, à chaque
  // usage, qu'il faut se garder — c'est exactement ce qu'on veut ici.
  media_publicRef?: string;
  media_name: string;
  scheduled_time: string;
  actual_start_time: string;
  duration_seconds: number;
  elapsed_seconds: number;
  remaining_seconds: number;
  current_time: string;
  // Programme en boucle : elapsed/remaining sont relatifs au tour en cours.
  loop?: boolean;
}

export interface ProgramVideoItem {
  broadcasted_media_id: number;
  media_id: number;
  media_uuid: string;
  // Optionnel tant que le microservice ffmpeg n'est pas redéployé : il ne
  // l'enverra qu'à partir de là. Le « ? » fait dire au compilateur, à chaque
  // usage, qu'il faut se garder — c'est exactement ce qu'on veut ici.
  media_publicRef?: string;
  media_name: string;
  scheduled_time: string;
  duration_seconds: number;
  // « Jouer en boucle » (absent d'un microservice pas encore redéployé).
  loop?: boolean;
}

export interface ProgramMetadata {
  current_time: string;
  count: number;
  videos: ProgramVideoItem[];
}

export interface XtreamConnectionCount {
  channelId: number;
  activeConnections: number;
  windowSeconds: number;
  totalConnections: number;
  lastConnectionAt: Date | null;
}

export const initializedXtreamConnectionCount = (): XtreamConnectionCount => ({
  channelId: 0,
  activeConnections: 0,
  windowSeconds: 60,
  totalConnections: 0,
  lastConnectionAt: null,
});

export interface XtreamConnectionLocation {
  username: string;
  ipAddress: string;
  lat: number;
  lon: number;
  city: string | null;
  country: string | null;
  isp: string | null;
  connectionDate: Date | null;
}

export interface XtreamConnectionLocations {
  channelId: number;
  windowSeconds: number;
  count: number;
  locations: XtreamConnectionLocation[];
}

export const initializedXtreamConnectionLocations = (): XtreamConnectionLocations => ({
  channelId: 0,
  windowSeconds: 60,
  count: 0,
  locations: [],
});